import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { signIn, signUp } from "@/lib/auth";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Button from "@/components/ui/Button";
import TextInput from "@/components/ui/TextInput";

export function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const justReset = params.get("reset") === "1";
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const result =
        mode === "signin"
          ? await signIn.email({ email, password })
          : await signUp.email({ email, password, name: name || email });
      if (result.error) {
        setErr(result.error.message ?? "Sign-in failed");
      } else {
        // Staff/admins land in the admin panel; everyone else in their portal.
        const role = (result.data?.user as { role?: string } | undefined)?.role;
        const dest =
          mode === "signin" && (role === "admin" || role === "staff") ? "/admin" : "/account";
        nav(dest);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="relative flex min-h-dvh items-center justify-center overflow-hidden px-6 py-16"
      style={{ backgroundColor: colors.cream }}
    >
      {/* soft brand blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-16 -top-24 h-72 w-72 rounded-full"
        style={{ background: colors.berryPink, opacity: 0.18, filter: "blur(64px)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-12 h-80 w-80 rounded-full"
        style={{ background: colors.lavender, opacity: 0.18, filter: "blur(64px)" }}
      />

      <div className="relative z-10 w-full max-w-sm">
        {/* brand wordmark */}
        <Link to="/" className="mb-6 flex flex-col items-center gap-2 no-underline">
          <img
            src="/logos/text-logo-purple.png"
            alt="A Few Loose Threads"
            className="h-10 w-auto"
            width={500}
            height={185}
          />
        </Link>

        {/* auth card */}
        <div
          className="rounded-[20px] border p-8 sm:p-10"
          style={{
            backgroundColor: "white",
            borderColor: `${colors.tan}26`,
            boxShadow: "0 20px 50px -28px rgba(8,45,61,0.35)",
          }}
        >
          <p
            className="mb-2 text-center text-xs uppercase"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              letterSpacing: "0.2em",
              color: colors.berryRose,
            }}
          >
            Members
          </p>
          <h1
            className="mb-7 text-center text-3xl"
            style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy }}
          >
            {mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>

          {justReset && mode === "signin" && (
            <p
              className="mb-5 rounded-lg px-3 py-2 text-center text-sm"
              style={{ background: "#606b4c", color: "#fff8ed", fontFamily: fonts.body, fontWeight: 600 }}
            >
              Password updated — sign in with your new password.
            </p>
          )}

          <form onSubmit={submit} className="grid gap-4">
            {mode === "signup" && (
              <TextInput
                label="Name"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
            <TextInput
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <TextInput
              label="Password"
              name="password"
              type="password"
              minLength={8}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {err && (
              <p
                className="text-center text-sm"
                style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}
              >
                {err}
              </p>
            )}
            <Button type="submit" className="mt-1 w-full" disabled={busy}>
              {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => {
                setErr(null);
                setMode(mode === "signin" ? "signup" : "signin");
              }}
              className="text-sm underline transition-opacity hover:opacity-70"
              style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.berryRose, letterSpacing: "0.03em" }}
            >
              {mode === "signin" ? "Create an account" : "Already have one? Sign in"}
            </button>
            {mode === "signin" && (
              <div className="mt-3">
                <Link
                  to="/forgot-password"
                  className="text-sm underline transition-opacity hover:opacity-70"
                  style={{ fontFamily: fonts.body, fontWeight: 600, color: `${colors.navy}99`, letterSpacing: "0.03em" }}
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>
        </div>

        <p
          className="mx-auto mt-6 max-w-xs text-center text-sm"
          style={{ fontFamily: fonts.body, color: `${colors.navy}88`, letterSpacing: "0.03em", lineHeight: 1.5 }}
        >
          Discord can be linked after sign-in from the community access page.
        </p>
      </div>
    </div>
  );
}
